/**
 * AdminSyncRunDetailPage - View a single sync job run with progress, items, summary
 *
 * Drives:
 * - GET /api/admin/sync/jobs/:id (detail)
 * - GET /api/admin/sync/jobs/:id/progress (polling)
 * - GET /api/admin/sync/jobs/:id/items (paginated)
 * - GET /api/admin/sync/jobs/:id/summary (aggregates)
 * - Links to /api/admin/sync/jobs/:id/export.json and .csv
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  RefreshCw,
  AlertTriangle,
  ChevronLeft,
  CheckCircle2,
  XCircle,
  Clock,
  Play,
  Download,
  Filter,
  Database,
  Search,
  Layers,
  HardDrive,
  FileJson,
  FileText,
  BarChart3,
} from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../lib/api';

// ============================================
// Types
// ============================================

interface SyncJobRun {
  id: string;
  job_type: string;
  selector: {
    ids?: Record<string, number[]>;
    cohort?: { type: string; cursor?: unknown };
  };
  media_types: string[];
  steps: {
    tmdb?: { enabled: boolean };
    omdb?: { enabled: boolean };
    meili?: { enabled: boolean };
    kv_warm?: { enabled: boolean };
  };
  max_items: number;
  dry_run: boolean;
  items_selected: number;
  items_queued: number;
  items_processing: number;
  items_succeeded: number;
  items_failed: number;
  items_skipped: number;
  created_count: number;
  updated_count: number;
  unchanged_count: number;
  started_at: string;
  finished_at: string | null;
  operator_user_id: string | null;
}

interface SyncJobProgress {
  job_run_id: string;
  status: 'running' | 'completed' | 'failed';
  items_selected: number;
  items_queued: number;
  items_processing: number;
  items_succeeded: number;
  items_failed: number;
  items_skipped: number;
  created_count: number;
  updated_count: number;
  unchanged_count: number;
  started_at: string;
  finished_at: string | null;
  progress_pct: number;
  dry_run: boolean;
}

interface SyncJobItem {
  id: string;
  job_run_id: string;
  media_type: string;
  tmdb_id: number;
  status: string;
  change_type: string | null;
  fields_changed: Record<string, unknown> | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

interface SyncJobItemsResponse {
  items: SyncJobItem[];
  next_cursor: string | null;
  total: number;
}

interface SyncJobSummary {
  job_run_id: string;
  counts_by_status: {
    queued: number;
    processing: number;
    succeeded: number;
    failed: number;
    skipped: number;
  };
  counts_by_change_type: {
    created: number;
    updated: number;
    unchanged: number;
    failed: number;
  };
  top_fields_changed: { field: string; count: number }[];
  error_codes: { code: string; count: number; sample_message: string }[];
}

// ============================================
// Helpers
// ============================================

type UiStatus = 'running' | 'has_errors' | 'complete' | 'dry_run';

function deriveStatus(job: SyncJobRun): UiStatus {
  if (job.dry_run && job.items_skipped === job.items_selected) return 'dry_run';
  if (!job.finished_at) return 'running';
  if (job.items_failed > 0) return 'has_errors';
  return 'complete';
}

function getStatusIcon(status: UiStatus) {
  switch (status) {
    case 'complete':
      return <CheckCircle2 size={16} className="text-green-400" />;
    case 'has_errors':
      return <XCircle size={16} className="text-red-400" />;
    case 'running':
      return <Play size={16} className="text-blue-400" />;
    case 'dry_run':
      return <Clock size={16} className="text-yellow-400" />;
    default:
      return <Clock size={16} className="text-slate-400" />;
  }
}

function getStatusColor(status: UiStatus): string {
  switch (status) {
    case 'complete':
      return 'text-green-400';
    case 'has_errors':
      return 'text-red-400';
    case 'running':
      return 'text-blue-400';
    case 'dry_run':
      return 'text-yellow-400';
    default:
      return 'text-slate-400';
  }
}

function getStatusLabel(status: UiStatus): string {
  switch (status) {
    case 'complete':
      return 'Complete';
    case 'has_errors':
      return 'Has Errors';
    case 'running':
      return 'Running';
    case 'dry_run':
      return 'Dry Run';
    default:
      return 'Unknown';
  }
}

function getItemStatusIcon(status: string) {
  switch (status) {
    case 'succeeded':
      return <CheckCircle2 size={14} className="text-green-400" />;
    case 'failed':
      return <XCircle size={14} className="text-red-400" />;
    case 'processing':
      return <Play size={14} className="text-blue-400" />;
    case 'skipped':
      return <Clock size={14} className="text-yellow-400" />;
    case 'queued':
    default:
      return <Clock size={14} className="text-slate-400" />;
  }
}

function getItemStatusColor(status: string): string {
  switch (status) {
    case 'succeeded':
      return 'text-green-400';
    case 'failed':
      return 'text-red-400';
    case 'processing':
      return 'text-blue-400';
    case 'skipped':
      return 'text-yellow-400';
    case 'queued':
    default:
      return 'text-slate-400';
  }
}

function formatDuration(startedAt: string, finishedAt: string | null): string {
  const start = new Date(startedAt).getTime();
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  const durationMs = end - start;

  if (durationMs < 1000) return '<1s';
  if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
  if (durationMs < 3600000) return `${Math.round(durationMs / 60000)}m`;
  return `${(durationMs / 3600000).toFixed(1)}h`;
}

function formatSelectorSummary(job: SyncJobRun): string {
  if (job.selector.cohort) {
    return `Cohort: ${job.selector.cohort.type}`;
  }
  if (job.selector.ids) {
    const counts = Object.entries(job.selector.ids)
      .map(([type, ids]) => `${ids.length} ${type}`)
      .join(', ');
    return `IDs: ${counts}`;
  }
  return 'Unknown';
}

function formatSteps(steps: SyncJobRun['steps']): string {
  const enabled = [];
  if (steps.tmdb?.enabled) enabled.push('TMDB');
  if (steps.omdb?.enabled) enabled.push('OMDB');
  if (steps.meili?.enabled) enabled.push('Meili');
  if (steps.kv_warm?.enabled) enabled.push('KV');
  return enabled.join(', ') || 'None';
}

function getStepIcon(step: string) {
  switch (step) {
    case 'tmdb':
      return <Database size={14} className="text-blue-400" />;
    case 'omdb':
      return <Layers size={14} className="text-yellow-400" />;
    case 'meili':
      return <Search size={14} className="text-purple-400" />;
    case 'kv_warm':
      return <HardDrive size={14} className="text-green-400" />;
    default:
      return null;
  }
}

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'queued', label: 'Queued' },
  { value: 'processing', label: 'Processing' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
];

const CHANGE_TYPE_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'created', label: 'Created' },
  { value: 'updated', label: 'Updated' },
  { value: 'unchanged', label: 'Unchanged' },
  { value: 'failed', label: 'Failed' },
];

const MEDIA_TYPE_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'movie', label: 'Movie' },
  { value: 'tv', label: 'TV' },
  { value: 'person', label: 'Person' },
];

// ============================================
// Component
// ============================================

export function AdminSyncRunDetailPage() {
  const { id: jobRunId } = useParams<{ id: string }>();
  const { session } = useAuth();

  // Job run state
  const [job, setJob] = useState<SyncJobRun | null>(null);
  const [jobLoading, setJobLoading] = useState(true);
  const [jobError, setJobError] = useState<string | null>(null);

  // Progress state (for polling)
  const [progress, setProgress] = useState<SyncJobProgress | null>(null);

  // Summary state
  const [summary, setSummary] = useState<SyncJobSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  // Items state
  const [statusFilter, setStatusFilter] = useState('');
  const [changeTypeFilter, setChangeTypeFilter] = useState('');
  const [mediaTypeFilter, setMediaTypeFilter] = useState('');
  const [itemsResponse, setItemsResponse] = useState<SyncJobItemsResponse | null>(null);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const limit = 50;

  // Fetch job detail
  const fetchJob = useCallback(async () => {
    if (!session?.access_token || !jobRunId) return;

    setJobLoading(true);
    setJobError(null);

    try {
      const data = await apiFetch<SyncJobRun>(`/admin/sync/jobs/${jobRunId}`, {
        token: session.access_token,
      });
      setJob(data);
    } catch (err) {
      setJobError(err instanceof Error ? err.message : 'Failed to fetch sync job');
    } finally {
      setJobLoading(false);
    }
  }, [session?.access_token, jobRunId]);

  // Fetch progress (lightweight, for polling)
  const fetchProgress = useCallback(async () => {
    if (!session?.access_token || !jobRunId) return;

    try {
      const data = await apiFetch<SyncJobProgress>(`/admin/sync/jobs/${jobRunId}/progress`, {
        token: session.access_token,
      });
      setProgress(data);

      // If job just finished, refresh the full job detail
      if (data.finished_at && job && !job.finished_at) {
        fetchJob();
      }
    } catch (err) {
      console.error('Failed to fetch progress:', err);
    }
  }, [session?.access_token, jobRunId, job, fetchJob]);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    if (!session?.access_token || !jobRunId) return;

    setSummaryLoading(true);

    try {
      const data = await apiFetch<SyncJobSummary>(`/admin/sync/jobs/${jobRunId}/summary`, {
        token: session.access_token,
      });
      setSummary(data);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    } finally {
      setSummaryLoading(false);
    }
  }, [session?.access_token, jobRunId]);

  // Fetch items
  const fetchItems = useCallback(
    async (cursor?: string) => {
      if (!session?.access_token || !jobRunId) return;

      setItemsLoading(true);
      setItemsError(null);

      try {
        let url = `/admin/sync/jobs/${jobRunId}/items?limit=${limit}`;
        if (statusFilter) url += `&status=${statusFilter}`;
        if (changeTypeFilter) url += `&change_type=${changeTypeFilter}`;
        if (mediaTypeFilter) url += `&media_type=${mediaTypeFilter}`;
        if (cursor) url += `&cursor=${encodeURIComponent(cursor)}`;

        const data = await apiFetch<SyncJobItemsResponse>(url, {
          token: session.access_token,
        });
        setItemsResponse(data);
      } catch (err) {
        setItemsError(err instanceof Error ? err.message : 'Failed to fetch items');
      } finally {
        setItemsLoading(false);
      }
    },
    [session?.access_token, jobRunId, statusFilter, changeTypeFilter, mediaTypeFilter]
  );

  // Initial fetch
  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  useEffect(() => {
    setCursorStack([]);
    fetchItems();
  }, [fetchItems]);

  // Poll progress while job is running (every 5 seconds)
  useEffect(() => {
    if (!job || job.finished_at) return;

    const interval = setInterval(() => {
      fetchProgress();
    }, 5000);

    return () => clearInterval(interval);
  }, [job, fetchProgress]);

  // Fetch summary when toggled on
  useEffect(() => {
    if (showSummary && !summary) {
      fetchSummary();
    }
  }, [showSummary, summary, fetchSummary]);

  const handleNextPage = () => {
    if (itemsResponse?.next_cursor) {
      if (itemsResponse.items.length > 0) {
        const lastItem = itemsResponse.items[itemsResponse.items.length - 1];
        setCursorStack((prev) => [...prev, `${lastItem.media_type}:${lastItem.tmdb_id}`]);
      }
      fetchItems(itemsResponse.next_cursor);
    }
  };

  const handlePrevPage = () => {
    if (cursorStack.length > 0) {
      const newStack = [...cursorStack];
      newStack.pop();
      setCursorStack(newStack);
      fetchItems(newStack.length > 0 ? newStack[newStack.length - 1] : undefined);
    }
  };

  // Export URL helpers
  const getExportUrl = (format: 'json' | 'csv') => {
    const baseUrl = import.meta.env.VITE_API_URL || '';
    let url = `${baseUrl}/api/admin/sync/jobs/${jobRunId}/export.${format}`;
    const params = new URLSearchParams();
    if (statusFilter) params.append('status', statusFilter);
    if (changeTypeFilter) params.append('change_type', changeTypeFilter);
    if (mediaTypeFilter) params.append('media_type', mediaTypeFilter);
    if (params.toString()) url += `?${params.toString()}`;
    return url;
  };

  if (jobLoading && !job) {
    return (
      <AdminLayout title="Sync Run" description="Loading...">
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={24} className="animate-spin text-slate-400" />
        </div>
      </AdminLayout>
    );
  }

  if (jobError) {
    return (
      <AdminLayout title="Sync Run" description="Error loading job">
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-400">
            <AlertTriangle size={16} />
            <span>{jobError}</span>
          </div>
        </div>
        <Link
          to="/admin/sync"
          className="mt-4 inline-flex items-center gap-2 text-teal-400 hover:text-teal-300"
        >
          <ChevronLeft size={16} />
          Back to Sync Control Plane
        </Link>
      </AdminLayout>
    );
  }

  const jobUiStatus = job ? deriveStatus(job) : null;
  const progressData = progress || (job ? {
    items_selected: job.items_selected,
    items_queued: job.items_queued,
    items_processing: job.items_processing,
    items_succeeded: job.items_succeeded,
    items_failed: job.items_failed,
    items_skipped: job.items_skipped,
    created_count: job.created_count,
    updated_count: job.updated_count,
    unchanged_count: job.unchanged_count,
    progress_pct: job.items_selected > 0
      ? Math.round(((job.items_succeeded + job.items_failed + job.items_skipped) / job.items_selected) * 100)
      : 0,
  } : null);

  return (
    <AdminLayout
      title={job ? `Sync Run: ${formatSelectorSummary(job)}` : 'Sync Run'}
      description={job?.id}
    >
      {/* Back link */}
      <Link
        to="/admin/sync"
        className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6"
      >
        <ChevronLeft size={16} />
        Back to Sync Control Plane
      </Link>

      {/* Job Summary Card */}
      {job && jobUiStatus && progressData && (
        <div className="p-4 bg-[#1A1F26] rounded-lg border border-white/[0.08] mb-6">
          {/* Status and Progress Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <p className="text-xs text-slate-400 mb-1">Status</p>
              <div className="flex items-center gap-2">
                {getStatusIcon(jobUiStatus)}
                <span className={`font-medium ${getStatusColor(jobUiStatus)}`}>
                  {getStatusLabel(jobUiStatus)}
                </span>
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Progress</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      jobUiStatus === 'has_errors' ? 'bg-red-400' :
                      jobUiStatus === 'complete' ? 'bg-green-400' :
                      jobUiStatus === 'dry_run' ? 'bg-yellow-400' :
                      'bg-blue-400'
                    }`}
                    style={{ width: `${progressData.progress_pct}%` }}
                  />
                </div>
                <span className="text-sm text-white font-medium">{progressData.progress_pct}%</span>
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Items</p>
              <p className="text-white font-medium">
                <span className="text-green-400">{progressData.items_succeeded}</span>
                <span className="text-slate-500"> / </span>
                <span>{progressData.items_selected}</span>
                {progressData.items_failed > 0 && (
                  <span className="text-red-400 ml-2">({progressData.items_failed} failed)</span>
                )}
              </p>
              {progressData.items_processing > 0 && (
                <p className="text-xs text-blue-400">{progressData.items_processing} processing</p>
              )}
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Duration</p>
              <p className="text-white font-medium">
                {formatDuration(job.started_at, job.finished_at)}
              </p>
            </div>
          </div>

          {/* Change Counts Row */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-4 py-3 px-3 bg-[#0F1419] rounded-lg">
            <div className="text-center">
              <p className="text-lg font-bold text-green-400">{progressData.created_count}</p>
              <p className="text-xs text-slate-400">Created</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-blue-400">{progressData.updated_count}</p>
              <p className="text-xs text-slate-400">Updated</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-slate-400">{progressData.unchanged_count}</p>
              <p className="text-xs text-slate-400">Unchanged</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-red-400">{progressData.items_failed}</p>
              <p className="text-xs text-slate-400">Failed</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-yellow-400">{progressData.items_skipped}</p>
              <p className="text-xs text-slate-400">Skipped</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-slate-300">{progressData.items_queued}</p>
              <p className="text-xs text-slate-400">Queued</p>
            </div>
          </div>

          {/* Metadata Row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            <div>
              <p className="text-xs text-slate-400 mb-1">Started</p>
              <p className="text-slate-300 text-sm">
                {new Date(job.started_at).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Finished</p>
              <p className="text-slate-300 text-sm">
                {job.finished_at ? new Date(job.finished_at).toLocaleString() : 'In progress...'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Media Types</p>
              <p className="text-slate-300 text-sm">{job.media_types.join(', ')}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Max Items</p>
              <p className="text-slate-300 text-sm">{job.max_items}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Steps (Summary)</p>
              <p className="text-slate-300 text-sm">{formatSteps(job.steps)}</p>
            </div>
          </div>

          {/* Steps */}
          <div className="mb-4">
            <p className="text-xs text-slate-400 mb-2">Steps</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(job.steps).map(([step, config]) => (
                <span
                  key={step}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs ${
                    config?.enabled
                      ? 'bg-teal-500/20 text-teal-400'
                      : 'bg-slate-700/50 text-slate-500'
                  }`}
                >
                  {getStepIcon(step)}
                  {step.toUpperCase()}
                </span>
              ))}
            </div>
          </div>

          {/* Selector Details */}
          <details className="mt-4">
            <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-300">
              View selector JSON
            </summary>
            <pre className="mt-2 text-xs text-slate-400 bg-[#0F1419] p-2 rounded overflow-x-auto">
              {JSON.stringify(job.selector, null, 2)}
            </pre>
          </details>
        </div>
      )}

      {/* Summary Section */}
      <div className="mb-6">
        <button
          onClick={() => setShowSummary(!showSummary)}
          className="flex items-center gap-2 text-sm text-teal-400 hover:text-teal-300 mb-4"
        >
          <BarChart3 size={16} />
          {showSummary ? 'Hide Summary' : 'Show Summary'}
        </button>

        {showSummary && (
          <div className="p-4 bg-[#1A1F26] rounded-lg border border-white/[0.08]">
            {summaryLoading && !summary ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw size={20} className="animate-spin text-slate-400" />
              </div>
            ) : summary ? (
              <div className="space-y-6">
                {/* Top Fields Changed */}
                {summary.top_fields_changed.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-white mb-3">Top Fields Changed</h4>
                    <div className="flex flex-wrap gap-2">
                      {summary.top_fields_changed.map(({ field, count }) => (
                        <span
                          key={field}
                          className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs"
                        >
                          {field}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Error Codes */}
                {summary.error_codes.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-white mb-3">Error Codes</h4>
                    <div className="space-y-2">
                      {summary.error_codes.map(({ code, count, sample_message }) => (
                        <div key={code} className="p-2 bg-red-500/10 rounded">
                          <div className="flex items-center justify-between">
                            <code className="text-xs text-red-400 bg-red-500/20 px-2 py-0.5 rounded">
                              {code}
                            </code>
                            <span className="text-xs text-slate-400">{count} occurrences</span>
                          </div>
                          {sample_message && (
                            <p className="text-xs text-slate-400 mt-1 truncate">{sample_message}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {summary.top_fields_changed.length === 0 && summary.error_codes.length === 0 && (
                  <p className="text-slate-400 text-sm">No field changes or errors recorded.</p>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Items Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Items</h3>
          <div className="flex items-center gap-2">
            {/* Export buttons */}
            <a
              href={getExportUrl('json')}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-300 bg-[#1A1F26] rounded-lg hover:text-white transition-colors"
              title="Export as JSON"
            >
              <Download size={14} />
              <FileJson size={14} />
              JSON
            </a>
            <a
              href={getExportUrl('csv')}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-300 bg-[#1A1F26] rounded-lg hover:text-white transition-colors"
              title="Export as CSV"
            >
              <Download size={14} />
              <FileText size={14} />
              CSV
            </a>
            <button
              onClick={() => {
                setCursorStack([]);
                fetchItems();
                fetchProgress();
              }}
              disabled={itemsLoading}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-300 bg-[#1A1F26] rounded-lg hover:text-white transition-colors"
            >
              <RefreshCw size={14} className={itemsLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-4 p-3 bg-[#1A1F26] rounded-lg">
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-slate-400" />
          </div>

          {/* Status filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Status:</span>
            <div className="flex gap-1">
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setStatusFilter(opt.value)}
                  className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                    statusFilter === opt.value
                      ? 'bg-teal-500 text-white'
                      : 'bg-[#0F1419] text-slate-400 hover:text-white'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Change type filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Change:</span>
            <div className="flex gap-1">
              {CHANGE_TYPE_FILTER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setChangeTypeFilter(opt.value)}
                  className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                    changeTypeFilter === opt.value
                      ? 'bg-teal-500 text-white'
                      : 'bg-[#0F1419] text-slate-400 hover:text-white'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Media type filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Type:</span>
            <div className="flex gap-1">
              {MEDIA_TYPE_FILTER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setMediaTypeFilter(opt.value)}
                  className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                    mediaTypeFilter === opt.value
                      ? 'bg-teal-500 text-white'
                      : 'bg-[#0F1419] text-slate-400 hover:text-white'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Error */}
        {itemsError && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-4">
            <div className="flex items-center gap-2 text-red-400">
              <AlertTriangle size={16} />
              <span>{itemsError}</span>
            </div>
          </div>
        )}

        {/* Items table */}
        {itemsResponse && (
          <div className="p-4 bg-[#1A1F26] rounded-lg border border-white/[0.08]">
            {itemsResponse.items.length === 0 ? (
              <p className="text-center text-slate-500 py-8">
                No items found matching the current filters
              </p>
            ) : (
              <>
                <div className="text-xs text-slate-400 mb-3">
                  Showing {itemsResponse.items.length} of {itemsResponse.total} items
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-400 border-b border-white/[0.08]">
                        <th className="pb-2 pr-4">Status</th>
                        <th className="pb-2 pr-4">TMDB ID</th>
                        <th className="pb-2 pr-4">Type</th>
                        <th className="pb-2 pr-4">Change</th>
                        <th className="pb-2 pr-4">Updated</th>
                        <th className="pb-2">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemsResponse.items.map((item) => (
                        <tr
                          key={`${item.media_type}-${item.tmdb_id}`}
                          className="border-b border-white/[0.04]"
                        >
                          <td className="py-2 pr-4">
                            <div className="flex items-center gap-2">
                              {getItemStatusIcon(item.status)}
                              <span className={getItemStatusColor(item.status)}>
                                {item.status}
                              </span>
                            </div>
                          </td>
                          <td className="py-2 pr-4">
                            <a
                              href={`/${item.media_type}/${item.tmdb_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-teal-400 hover:text-teal-300"
                            >
                              {item.tmdb_id}
                            </a>
                          </td>
                          <td className="py-2 pr-4 text-slate-300">{item.media_type}</td>
                          <td className="py-2 pr-4">
                            {item.change_type ? (
                              <span
                                className={`text-xs px-1.5 py-0.5 rounded ${
                                  item.change_type === 'created'
                                    ? 'bg-green-500/20 text-green-400'
                                    : item.change_type === 'updated'
                                    ? 'bg-blue-500/20 text-blue-400'
                                    : item.change_type === 'unchanged'
                                    ? 'bg-slate-500/20 text-slate-400'
                                    : 'bg-red-500/20 text-red-400'
                                }`}
                              >
                                {item.change_type}
                              </span>
                            ) : (
                              <span className="text-slate-500 text-xs">-</span>
                            )}
                          </td>
                          <td className="py-2 pr-4 text-slate-400 text-xs">
                            {new Date(item.updated_at).toLocaleString()}
                          </td>
                          <td className="py-2 max-w-xs">
                            {item.error_code || item.error_message ? (
                              <span
                                className="text-xs text-red-400 truncate block"
                                title={`${item.error_code || ''}: ${item.error_message || ''}`}
                              >
                                {item.error_code && (
                                  <code className="bg-red-500/10 px-1 rounded mr-1">
                                    {item.error_code}
                                  </code>
                                )}
                                {item.error_message && (
                                  <>
                                    {item.error_message.substring(0, 60)}
                                    {item.error_message.length > 60 && '...'}
                                  </>
                                )}
                              </span>
                            ) : (
                              <span className="text-slate-500 text-xs">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/[0.08]">
                  <p className="text-sm text-slate-400">Page {cursorStack.length + 1}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={cursorStack.length === 0 || itemsLoading}
                      className="px-3 py-1.5 text-sm text-slate-300 bg-[#0F1419] rounded-lg hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={!itemsResponse.next_cursor || itemsLoading}
                      className="px-3 py-1.5 text-sm text-slate-300 bg-[#0F1419] rounded-lg hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
